import { capitalize, isEqual, lowerCase } from 'lodash';
import {
  ColumnDensity,
  components,
  paths,
  PatientPreferences,
  SORT_ORDER,
} from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import {
  usePatientsListQuery,
  usePatientsMetadataQuery,
  usePatientTablePreferencesQuery,
  useUpdatePatientTablePreferencesMutation,
} from '@common/cache';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridDensity,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { TruncatedTextWithConditionalTooltip, MinutesChip } from '@components';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  theme,
  usePatientsStore,
  PersistedPatientsColumn,
  IPatientsTableFilters,
  emptyPatientsFilters,
} from '@store';
import { formatAddress } from '@util/address';
import {
  buildGridColumns,
  getCellTextWidth,
  getReorderedColumns,
} from '@util/dataGrid';
import { getAccountStatusColor } from '@util/user';
import { GridClickableCell } from '@ui';
import { convertGridDensityToPreferencesDensity } from 'components/TablePreferences';

export type IPatientsTableEntry = components['schemas']['FilteredListPatient'];

type BuildPatientsColumnsArgs = {
  density?: ColumnDensity;
  handleOpenCMActivitiesModal: (patientId: string) => void;
  handleFilterByLocation: (buildingId: string) => void;
};

const buildPatientsColumns = ({
  density,
  handleOpenCMActivitiesModal,
  handleFilterByLocation,
}: BuildPatientsColumnsArgs): GridColDef<IPatientsTableEntry>[] => {
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 150,
      minWidth: 150,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <TruncatedTextWithConditionalTooltip
            variant='body2'
            text={params.row.display_id}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
        </GridClickableCell>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 180,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <TruncatedTextWithConditionalTooltip
              variant='body2'
              text={params.row.name}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                variant='caption'
                color='textSecondary'
                text={capitalize(params.row.gender)}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'birthdate',
      headerName: 'Date of birth',
      width: 130,
      minWidth: 130,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell
          to={`/patient/${params.row.id}`}
          style={{ minWidth: 120 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
            <TruncatedTextWithConditionalTooltip
              text={
                params.row.birthdate
                  ? new Date(params.row.birthdate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : ''
              }
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.age}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'minutes_this_month',
      headerName: 'CM Minutes',
      width: 140,
      minWidth: 140,
      sortable: true,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <Box
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleOpenCMActivitiesModal(params.row.id);
              }}
            >
              <MinutesChip minutes={params.row.minutes_this_month} clickable />
            </Box>
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'mbi',
      headerName: 'Primary insurance',
      minWidth: 160,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => {
        const isMedicare = params.row.insurance_type === 'medicare';
        const hasInsurance = !!params.row.insurance_type;

        const primaryText = isMedicare
          ? params.row.mbi
          : hasInsurance
            ? 'Other'
            : '-';

        const secondaryText =
          hasInsurance && params.row.medicare_payer_name
            ? params.row.medicare_payer_name
            : undefined;

        return (
          <GridClickableCell to={`/patient/${params.row.id}`}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <TruncatedTextWithConditionalTooltip
                width={getCellTextWidth(params.colDef.computedWidth)}
                text={primaryText}
                sx={{ fontSize: '14px' }}
              />
              {density !== 'dense' && secondaryText && (
                <TruncatedTextWithConditionalTooltip
                  text={secondaryText}
                  variant='body2'
                  color='textSecondary'
                  sx={{ fontSize: '12px' }}
                  width={getCellTextWidth(params.colDef.computedWidth)}
                />
              )}
            </Box>
          </GridClickableCell>
        );
      },
    },
    {
      field: 'account_status',
      headerName: 'Account status',
      width: 140,
      minWidth: 140,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Chip
              sx={{
                backgroundColor: getAccountStatusColor(
                  params.row.account_status,
                ),
                color:
                  params.row.account_status === 'Inactive'
                    ? 'rgba(0, 0, 0, 0.6)'
                    : theme.palette.common.white,
                height: '24px',
              }}
              label={params.row.account_status || 'Status unknown'}
            />
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'guide_status',
      headerName: 'GUIDE status',
      minWidth: 160,
      flex: 1,
      sortable: false,
      filterable: true,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TruncatedTextWithConditionalTooltip
              text={params.row.guide_status}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.guide_tier}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'programs',
      headerName: 'Service Lines & Programs',
      minWidth: 160,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 120,
              alignItems: 'flex-start',
              gap: 0,
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={
                params.row.programs?.length > 0
                  ? params.row.programs[0]
                  : 'None'
              }
              {...(!params.row.programs?.length && {
                color: 'textSecondary',
              })}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {params.row.programs?.length > 1 && density !== 'dense' && (
              <Tooltip
                title={
                  <Box sx={{ whiteSpace: 'pre-line' }}>
                    {params.row.programs.slice(1).join('\n')}
                  </Box>
                }
                arrow
              >
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    sx={{ fontSize: '12px' }}
                  >
                    {` + ${params.row.programs.length - 1} more`}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'open_task_count',
      headerName: 'Open tasks',
      width: 110,
      minWidth: 110,
      sortable: true,
      filterable: false,
      headerAlign: 'center',
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Typography
            variant='body2'
            sx={{ textAlign: 'center', width: '100%' }}
          >
            {params.row.open_task_count}
          </Typography>
        </GridClickableCell>
      ),
    },
    {
      field: 'provider_name',
      headerName: 'PCP',
      width: 160,
      minWidth: 160,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TruncatedTextWithConditionalTooltip
              text={params.row.provider_name}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.provider_practice_name}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'practice_name',
      headerName: 'Practice',
      minWidth: 160,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <TruncatedTextWithConditionalTooltip
            text={params.row.practice_name}
            sx={{ fontSize: '14px' }}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
        </GridClickableCell>
      ),
    },
    {
      field: 'care_navigator_name',
      headerName: 'Care navigator',
      width: 170,
      minWidth: 170,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TruncatedTextWithConditionalTooltip
              text={params.row.care_navigator_name}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.care_navigator_team_name}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'building_name',
      headerName: 'Location',
      width: 180,
      minWidth: 180,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {params.row.building_id &&
            params.row.building_name &&
            params.row.operator_id &&
            params.row.operator_name ? (
              <>
                <Tooltip
                  title={
                    <Box sx={{ whiteSpace: 'pre-line' }}>
                      {formatAddress(params.row.building_address)}
                    </Box>
                  }
                  arrow
                >
                  <Box
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleFilterByLocation(params.row.building_id);
                    }}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                    }}
                  >
                    <TruncatedTextWithConditionalTooltip
                      text={params.row.building_name}
                      placement='bottom'
                      variant='body2'
                      sx={{
                        fontSize: '14px',
                        textDecoration: 'underline',
                        color: 'primary.main',
                      }}
                      width={getCellTextWidth(params.colDef.computedWidth)}
                    />
                  </Box>
                </Tooltip>

                {density !== 'dense' && (
                  <TruncatedTextWithConditionalTooltip
                    text={params.row.operator_name}
                    placement='bottom'
                    variant='body2'
                    color='textSecondary'
                    sx={{ fontSize: '12px' }}
                    width={getCellTextWidth(params.colDef.computedWidth)}
                  />
                )}
              </>
            ) : (
              <Typography variant='body1' sx={{ fontSize: '14px' }}>
                Home
              </Typography>
            )}
          </Box>
        </GridClickableCell>
      ),
    },
  ] as GridColDef<IPatientsTableEntry>[];

  return columns;
};

export type { IPatientsTableFilters };

const emptyFilters = emptyPatientsFilters;

const MIN_VISIBLE_COLUMNS = 4;

export const usePatientsTable = (variant: 'allPatients' | 'myPatients') => {
  const location = useLocation();
  const apiRef = useGridApiRef();
  const [isSideFiltersOpen, setIsSideFiltersOpen] = useState(false);
  const [isResetViewModalOpen, setIsResetViewModalOpen] = useState(false);
  const [isCMActivitiesModalOpen, setIsCMActivitiesModalOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState('');

  const filters = usePatientsStore(s => s.filters);
  const setFilters = usePatientsStore(s => s.setFilters);
  const resetFilters = usePatientsStore(s => s.resetFilters);
  const additionalFilters = usePatientsStore(s => s.additionalFilters);
  const setAdditionalFilters = usePatientsStore(s => s.setAdditionalFilters);

  const { data: preferences, isSuccess: preferencesLoaded } =
    usePatientTablePreferencesQuery();

  useEffect(() => {
    setFilters({
      ...(location.state && { building_ids: [location.state] }),
      ...(preferences?.sort_by && { sort_by: preferences.sort_by }),
      ...(preferences?.sort_order && {
        sort_order: lowerCase(preferences.sort_order) as SORT_ORDER,
      }),
      ...(preferences?.page_size && { limit: preferences.page_size }),
    });
    if (location.state) {
      setAdditionalFilters(JSON.stringify({ building_id: location.state }));
    }
  }, [
    preferencesLoaded,
    preferences,
    location.state,
    setFilters,
    setAdditionalFilters,
  ]);

  useEffect(() => {
    return () => resetFilters();
  }, []);

  const { data: metadata } = usePatientsMetadataQuery();

  const updatePreferencesMutation = useUpdatePatientTablePreferencesMutation();

  const handleUpdatePreferences = async (
    newPreferences: Partial<
      paths['/preferences/patient-table']['patch']['requestBody']['content']['application/json']
    >,
  ) => {
    await updatePreferencesMutation.mutateAsync(newPreferences);
  };

  const handleChangeOrder = async (field: string, sort: GridSortDirection) => {
    await updatePreferencesMutation.mutateAsync({
      sort_by:
        field as paths['/preferences/patient-table']['patch']['requestBody']['content']['application/json']['sort_by'],
      sort_order: sort ? (sort?.toUpperCase() as 'ASC' | 'DESC') : undefined,
    });
  };

  const {
    data = { patients: [], count: 0 },
    isFetching,
    isRefetching,
  } = usePatientsListQuery({
    ...filters,
    my_patients: variant === 'myPatients',
    additionalFilters,
    enabled: preferencesLoaded,
  });

  const handleOpenResetViewModal = () => {
    setIsResetViewModalOpen(true);
  };

  const handleCloseResetViewModal = () => {
    setIsResetViewModalOpen(false);
  };

  const handleOpenCMActivitiesModal = (patient_id: string) => {
    setIsCMActivitiesModalOpen(true);
    setSelectedPatientId(patient_id);
  };

  const handleCloseCMActivitiesModal = () => {
    setIsCMActivitiesModalOpen(false);
    setSelectedPatientId('');
  };

  const handleToggleSideFilters = () => {
    setIsSideFiltersOpen(prev => !prev);
  };

  const handleSearchChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      search: event.target.value,
      page: 1,
    });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    if (isFetching || isRefetching) return;

    setFilters({ limit: model.pageSize, page: model.page + 1 });
    if (preferences?.page_size !== model.pageSize)
      updatePreferencesMutation.mutateAsync({
        page_size: model.pageSize,
      });
  };

  const handleSelectPartner = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      partner_id:
        event.target.value === t('common.all') ? '' : event.target.value,
      practice_ids: [],
      page: 1,
    });
  };

  const handleSelectPractices = (newValues: string[]) => {
    setFilters({
      practice_ids: newValues,
    });
  };

  const handleSelectBuildings = (newValues: string[]) => {
    setFilters({
      building_ids: newValues,
    });
  };

  const handleSelectProviders = (newValues: string[]) => {
    setFilters({
      provider_ids: newValues,
    });
  };

  const handleSelectCareNavigatorIds = (newValues: string[]) => {
    setFilters({
      care_navigator_ids: newValues,
    });
  };

  const handleSelectCareManagerIds = (newValues: string[]) => {
    setFilters({
      care_manager_ids: newValues,
    });
  };

  const handleSelectWellnessCoordinatorIds = (newValues: string[]) => {
    setFilters({
      wellness_coordinator_ids: newValues,
    });
  };

  const handleSelectAccountStatus = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      account_status:
        event.target.value === t('common.all') ? '' : event.target.value,
    });
  };

  const handleSelectProgramName = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      program_name:
        event.target.value === t('common.all') ? '' : event.target.value,
    });
  };

  const handleSelectProgramStatus = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      program_status:
        event.target.value === t('common.all') ? '' : event.target.value,
    });
  };

  const handleSelectProgramTier = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({
      program_tier:
        event.target.value === t('common.all') ? '' : event.target.value,
    });
  };

  const handleResetFilter = () => {
    setFilters({
      ...emptyFilters,
      search: filters.search,
      partner_id: filters.partner_id,
    });
    setAdditionalFilters('');
  };

  const handleFullReset = () => {
    resetView();
    setFilters({
      ...emptyFilters,
    });
    setAdditionalFilters('');
  };

  const applyAdditionalFilters = () => {
    setFilters({ page: 1 });
    setAdditionalFilters(JSON.stringify({ ...filters, page: 1 }));
  };

  const handleFilterByLocation = (building_id: string) => {
    setFilters({
      building_ids: [building_id],
      page: 1,
    });
    setAdditionalFilters(JSON.stringify({ ...filters, building_id, page: 1 }));
  };

  const handleUpdateVisibilityModel = (model: GridColumnVisibilityModel) => {
    type PatientColumns = PatientPreferences['columns'];
    type PatientColumnKey = keyof PatientColumns;

    const currentColumns: PatientPreferences['columns'] =
      preferences?.columns ?? {};
    const updatedColumns: PatientPreferences['columns'] = {};

    const fields = Object.keys(currentColumns) as PatientColumnKey[];

    const selectedCount = fields.filter(field => {
      const config = currentColumns[field];
      return config && config.column_display !== 'hidden';
    }).length;

    const totalCount = fields.length;
    const isEmptyModel = Object.keys(model).length === 0;

    const shouldShowAll = isEmptyModel && selectedCount < totalCount;
    const shouldHideAll = isEmptyModel && selectedCount === totalCount;

    for (const field of fields) {
      const config = currentColumns[field];

      if (!config) {
        continue;
      }

      let nextDisplay = config.column_display;

      if (shouldShowAll) {
        if (config.column_display === 'hidden') {
          nextDisplay = 'display';
        }
      } else if (shouldHideAll) {
        nextDisplay = 'hidden';
      } else {
        const isVisible = model[field];
        const wasHidden = config.column_display === 'hidden';

        if (!isVisible) {
          nextDisplay = 'hidden';
        }

        if (isVisible === true && wasHidden) {
          nextDisplay = 'display';
        }
      }

      updatedColumns[field] = {
        ...config,
        column_display: nextDisplay,
      };
    }

    handleUpdatePreferences({ columns: updatedColumns });
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const onDensityChange = (density: GridDensity) => {
    const adaptedDensity = convertGridDensityToPreferencesDensity(density);
    handleUpdatePreferences({
      density: adaptedDensity as ColumnDensity,
    });
  };

  const navigate = useNavigate();

  const handleRowClick = (
    e: React.MouseEvent | React.KeyboardEvent,
    id: string,
  ) => {
    const url = `/patient/${id}`;

    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  const FILTER_CARD_KEYS = [
    'account_status',
    'program_name',
    'program_status',
    'program_tier',
    'building_ids',
    'practice_ids',
    'provider_ids',
    'care_manager_ids',
    'care_navigator_ids',
    'wellness_coordinator_ids',
  ] as const;

  const isFilterDisabled = FILTER_CARD_KEYS.every(key =>
    isEqual(filters[key], emptyFilters[key]),
  );

  const persistedColumns = usePatientsStore(s => s.columns);
  const setPersistedColumns = usePatientsStore(s => s.setColumns);
  const pinnedColumns = usePatientsStore(s => s.pinnedColumns);
  const setPinnedColumns = usePatientsStore(s => s.setPinnedColumns);
  const resetView = usePatientsStore(s => s.resetView);

  const runtimeColumns = buildPatientsColumns({
    density: preferences?.density,
    handleFilterByLocation,
    handleOpenCMActivitiesModal,
  });

  const columns = buildGridColumns<
    IPatientsTableEntry,
    PersistedPatientsColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<IPatientsTableEntry>,
  ): PersistedPatientsColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );

    setPersistedColumns(next.map(toPersistedColumn));
  };

  const visibilityModel: Record<string, boolean> = {};
  Object.entries(preferences?.columns ?? {}).forEach(([field, config]) => {
    visibilityModel[field] = config?.column_display !== 'hidden';
  });

  const visibleColumnsCount =
    Object.values(visibilityModel).filter(Boolean).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;

  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  return {
    apiRef,
    pinnedColumns,
    handlePinnedColumnsChange,
    visibilityModel,
    handleUpdateVisibilityModel,
    onDensityChange,
    filters,
    isInitialFilters: JSON.stringify(filters) === JSON.stringify(emptyFilters),
    rows: data.patients,
    columns: columnsWithHideable,
    totalCount: data.count,
    loading: isFetching || isRefetching,
    accountStatusOptions: (metadata?.account_status_options ?? [])
      .filter(o => o !== 'Ineligible')
      .map(o => ({
        value: o,
        label: o,
      })),
    programNameOptions: (metadata?.program_filter_options ?? []).map(o => ({
      value: o,
      label: o,
    })),
    programStatusOptions: (
      metadata?.program_status_options[filters.program_name || 'All'] ?? []
    ).map(o => ({
      value: o,
      label: o,
    })),
    programTierOptions: (
      metadata?.program_tier_options[filters.program_name || 'All'] ?? []
    ).map(o => ({
      value: o,
      label: o,
    })),
    buildingOptions: (metadata?.buildings ?? []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    providerOptions: (metadata?.provider_filter_options ?? []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnersOptions: (metadata?.partners ?? []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    practiceOptions: (metadata?.practices ?? []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    careManagerOptions: [
      { value: 'null', label: 'None' },
      ...(metadata?.care_managers ?? []).map(o => ({
        value: o.id,
        label: o.name,
      })),
    ],
    careNavigatorOptions: [
      { value: 'null', label: 'None' },
      ...(metadata?.care_navigators ?? []).map(o => ({
        value: o.id,
        label: o.name,
      })),
    ],
    welnessCoordinatorIds: [
      { value: 'null', label: 'None' },
      ...(metadata?.wellness_coordinators ?? []).map(o => ({
        value: o.id,
        label: o.name,
      })),
    ],
    handleSearchChange,
    handleSelectAccountStatus,
    handleSelectProgramTier,
    handleSelectProgramStatus,
    handleSelectProgramName,
    handleSelectCareManagerIds,
    handleSelectCareNavigatorIds,
    handleSelectProviders,
    handleSelectBuildings,
    handleSelectPractices,
    handleSelectWellnessCoordinatorIds,
    onPageModelChange,
    handleResetFilter,
    isFilterDisabled,
    handleToggleSideFilters,
    isSideFiltersOpen,
    handleSelectPartner,
    applyAdditionalFilters,
    handleRowClick,
    isCMActivitiesModalOpen,
    handleCloseCMActivitiesModal,
    selectedPatientId,
    preferences,
    handleUpdatePreferences,
    handleChangeOrder,
    handleFullReset,
    handleColumnOrderChange,
    handleColumnWidthChange,
    isResetViewModalOpen,
    handleOpenResetViewModal,
    handleCloseResetViewModal,
    initialSortFilters: {
      sort_order: 'asc',
      sort_by: 'name',
    },
  };
};
